import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Slider } from '../../../components/ui/slider';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Card } from '../../../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

export default function TwinProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [twinName, setTwinName] = useState('');
  const [language, setLanguage] = useState('en');
  const [responseTone, setResponseTone] = useState('casual');
  const [personality, setPersonality] = useState({
    analytical: 5,
    empathetic: 5,
    direct: 5,
    creative: 5,
    pragmatic: 5,
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await apiClient.get('/personal/profile');
      const data = response.data;
      
      // Handle EMPTY representation state
      // CONSTITUTIONAL PRINCIPLE: NO EVIDENCE = valid epistemic state
      if (data.representationState === 'EMPTY' || !data.hasPersonalEvidence) {
        // Valid empty state - use defaults but don't show error
        // User has no personal evidence yet, which is a valid HumaTwin state
        setTwinName('');
        setLanguage(data.language || 'en');
        setResponseTone('casual');
        // Keep default personality values - don't fabricate
        return;
      }
      
      // Populated profile - use actual values
      setTwinName(data.twinName || '');
      setLanguage(data.language || 'en');
      setResponseTone(data.responseTone || 'casual');
      if (data.personality && Object.keys(data.personality).length > 0) {
        setPersonality(data.personality);
      }
    } catch (error) {
      // Only log non-404 errors - 404 should no longer occur
      // but handle gracefully if it does
      console.error('Failed to fetch profile:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await apiClient.post('/personal/profile', {
        twinName,
        language,
        responseTone,
        personality,
        trainingModes: [],
        introductionSamples: [],
      });
      toast.success(t('success.updated'));
      navigate('/personal/twin');
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="twin-profile-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/personal/twin')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">{t('twin.profile')}</h1>
          <p className="text-gray-600 mt-1">{t('twin.configureIdentity')}</p>
        </div>
      </div>

      <Card className="p-6 rounded-2xl space-y-6">
        <div>
          <Label htmlFor="twinName">Twin Name *</Label>
          <Input
            id="twinName"
            value={twinName}
            onChange={(e) => setTwinName(e.target.value)}
            placeholder="e.g., Alex, My Twin, Personal Coach"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="language">{t('twin.language')}</Label>
          <select
            id="language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30"
          >
            <option value="en">{t('languages.english')}</option>
            <option value="es">{t('languages.spanish')}</option>
            <option value="fr">{t('languages.french')}</option>
            <option value="de">{t('languages.german')}</option>
          </select>
        </div>

        <div>
          <Label>{t('twin.responseTone')}</Label>
          <RadioGroup value={responseTone} onValueChange={setResponseTone} className="mt-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="formal" id="formal" />
              <Label htmlFor="formal">{t('twin.formal')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="casual" id="casual" />
              <Label htmlFor="casual">{t('twin.casual')}</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-4">
          <Label>{t('twin.personalityTraits')}</Label>
          <p className="text-sm text-gray-600">
            Adjust these sliders to define your twin's personality characteristics
          </p>
          {Object.keys(personality).map((trait) => (
            <div key={trait}>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium capitalize">{trait}</span>
                <span className="text-sm text-gray-500">{personality[trait]}</span>
              </div>
              <Slider
                value={[personality[trait]]}
                onValueChange={([value]) =>
                  setPersonality({ ...personality, [trait]: value })
                }
                min={1}
                max={10}
                step={1}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/personal/twin')}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={loading || !twinName}>
            {loading ? t('skills.saving') : t('common.save')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
